<script lang="ts" setup>
import { addBreadcrumb } from '@sentry/capacitor';

interface Props{
  startActive?: boolean;
  sleepTime?: number;
}

const props = withDefaults(defineProps<Props>(), {
  startActive: false,
  sleepTime: 1000 * 60 * 5
});

defineExpose({ startMfa, stopMfa });

const loginAPI = useLoginAPI();
const userConfigurationStore = useUserConfigurationStore();
const { t, d } = useEkI18n();
const { openToast } = useUserMessages();

const mfaResponse = ref<MfaCodeResponse | undefined>();
const countdownTime = ref(0);

const displayStartTime = ref(0);
const isLoading = ref(false);

let interval: ReturnType<typeof setInterval> | undefined;
const intervalTime = 1000;
const codeDuration = 1000 * 60 * 3;

const progressBarMode = computed(() => isLoading.value ? 'indeterminate' : 'determinate');
const isStopped = computed(() => mfaResponse.value === undefined);
const progress = computed(() => Math.min(1, 1 - (countdownTime.value / codeDuration)));

const codeLabel = computed(() => {
  if (isLoading.value) {
    return t('loading.loading');
  }

  if (mfaResponse.value !== undefined) {
    return mfaResponse.value.Code;
  }

  return t('mfa.tap');
});

const expirationLabel = computed(() => {
  // countdownTime is compared against -1000 to allow for 00:00 to be displayed
  if (mfaResponse.value === undefined || countdownTime.value <= -1000) {
    return '';
  }

  // Round up to the nearest second since we dont display milliseconds
  const duration = Math.ceil(countdownTime.value / 1000) * 1000;

  return d(new Date(duration), 'dateFormats.mfaCountdown');
});

if (props.startActive) {
  startMfa();
}

async function onClick() {
  if (isLoading.value) {
    return;
  }

  if (isStopped.value) {
    await startMfa();

    return;
  }

  await copyStringToClipboard(codeLabel.value);
  await openToast(t('mfa.copiedToClipboard'), ToastType.INFO);
}

async function getMfaCode() {
  if (userConfigurationStore.userConfiguration === undefined) {
    addBreadcrumb({
      category: 'MFA',
      message: 'Missing user configuration, when getting MFA code',
      level: 'error'
    });

    return;
  }

  const request: MfaCodeRequest = {
    Username: userConfigurationStore.userConfiguration.UserName,
    SiteName: userConfigurationStore.userConfiguration.SiteName
  };

  isLoading.value = true;
  const response = await loginAPI.mfaCode(request);
  isLoading.value = false;

  if (response === undefined) {
    stopMfa();

    return;
  }

  mfaResponse.value = response.Data;
  countdownTime.value = 0;
  tickDisplay();
}

async function startMfa() {
  if (mfaResponse.value !== undefined) {
    stopMfa();

    return;
  }

  await getMfaCode();

  if (mfaResponse.value === undefined) {
    return;
  }

  displayStartTime.value = new Date().getTime();
  interval = setInterval(tickDisplay, intervalTime);
}

function stopMfa() {
  clearInterval(interval);
  mfaResponse.value = undefined;
}

async function tickDisplay() {
  if (mfaResponse.value === undefined) {
    return;
  }

  countdownTime.value = new Date(mfaResponse.value.Expiration).getTime() - new Date().getTime();

  // countdownTime is compared against -1000 to allow for 00:00 to be displayed
  if (countdownTime.value > -1000) {
    return;
  }

  if (displayStartTime.value + props.sleepTime < new Date().getTime()) {
    stopMfa();

    return;
  }

  clearInterval(interval);
  await getMfaCode();

  if (mfaResponse.value === undefined) {
    return;
  }

  interval = setInterval(tickDisplay, intervalTime);
}

</script>

<template>
  <IonCard
    class="container"
    button
    @click="onClick"
  >
    <IonCardHeader class="header">
      <IonLabel>{{ t('mfa.codeToWeb') }}</IonLabel>
      <IonLabel :class="{invisible: isStopped}">
        {{ expirationLabel }}
      </IonLabel>
    </IonCardHeader>
    <IonCardContent class="code">
      {{ codeLabel }}
    </IonCardContent>
    <IonProgressBar
      :type="progressBarMode"
      :value="progress"
      :class="{invisible: isStopped && isLoading === false}"
    />
  </IonCard>
</template>

<style scoped>
.code{
  text-align: center;
  font-size: xx-large;
}

.header{
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.invisible{
  visibility: hidden;
}
</style>
